import Dependency from './dependency';

/**
 * Class used to create paths based on the 'dependencies' attribute
 * Path is updated on every panel change (listening to custom event 'panelChanged') and select field change
 * This class it NOT exported as a class, we export an instance of this class
 * This is done to use the same instance of this class in panels.js and formvalidator.js
 */

class PathCreator {
    #dependenciesStringList;
    #dependencies;
    #path;

    constructor() {
        this.init();
    }

    /**
     * Initializes the pathCreator class
     * Adds event listener to listen for the 'panelChanged' event
     * Updates path value
     */
    init() {
        // Listening to the custom event fired on panel change
        document.addEventListener('panelChanged', (e) => {
            const dependenciesField = e.detail.panels[e.detail.newPanelIndex].querySelector('select[dependencies]:not(select[dependencies=""])');
            if(!dependenciesField) return;

            // Update path on select field change
            dependenciesField.addEventListener('change', () => {
                this.findPath(
                    e.detail.form,
                    e.detail.panels[e.detail.newPanelIndex]
                )
            })

            // Update values (path) on panel change
            this.updateValues(dependenciesField.getAttribute('dependencies'), e);
        })
    }

    /**
     * Updates path value based on the dependenciesString given
     * @param {String} dependenciesString Value of the 'dependencies' attribute of the field
     * @param {Event} event panelChanged event
     */
    updateValues(dependenciesString, event) {
        if(!dependenciesString) return;
        // Dependencies list
        this.#dependenciesStringList = dependenciesString.split(";");
        this.#dependencies = [];
        this.#path = [];

        for(const dependency of this.#dependenciesStringList)
            this.#dependencies.push(new Dependency(dependency));

        // Finds the path given the active panel
        this.findPath(event.detail.form, event.detail.panels[event.detail.newPanelIndex]);
    }

    /**
     * Finds the new path, or resets the current path if there is no match
     * @param {Node} form Form element
     * @param {Node} panel Active panel element
     */
    findPath(form, panel) {
        if(!this.#dependencies || this.#dependencies.length < 1) return;
        for (const dependency of this.#dependencies) {
            const selectField = form.querySelector(`select[name$="[${dependency.getFieldName()}]"]`);
            const dependencyField = panel.querySelector('select[dependencies]:not(select[dependencies=""])');
            if (!dependencyField) return;
            // If there is no select field, then update the path based only on the field with 'dependencies' attribute
            if(!selectField) {
                if(dependencyField.value !== dependency.getDependencyFieldValue()) {
                    this.#path = [];
                    continue;
                };
                this.#path = dependency.getPath();
                // console.log(`Path found: ${this.#path}`);
                return;
            }

            const selectedValue = selectField.value;

            // If the PREVIOUSLY selected value has no path assigned to it, reset the path
            // selectedValue is the value from the NAMED field from 'dependencies' attribute
            if(selectedValue !== dependency.getFieldValue()) {
                this.#path = [];
                continue;
            };

            // If the CURRENTLY selected value has no path assigned to it, reset the path
            // The dependencyField.value is the value from the UNNAMED field from 'dependencies' attribute
            if(dependencyField.value !== dependency.getDependencyFieldValue()) {
                this.#path = [];
                continue;
            };

            // Update the path when the fields match
            this.#path = dependency.getPath();
            // console.log(`Path found: ${this.#path}`);
            return;
        }
    }

    getPath() {
        return this.#path;
    }

    setPath(path) {
        this.#path = path;
    }
}

export default new PathCreator();